import { useEffect, useState } from 'react';
import {
  TRANSPORT_MODES,
  TRANSPORT_MODE_HINT,
  TRANSPORT_MODE_LABEL,
  type FaqSection,
  type PlatformSettings,
} from 'shared';
import { api } from '../api';
import NumberField from '../NumberField';
import AdminList from '../AdminList';
import AdminSheet from '../AdminSheet';
import { alert, confirm, notify, openTelegram } from '../telegram';

/** Черновик раздела: пункты правятся одним текстовым полем, по строке на пункт. */
interface Draft {
  title: string;
  itemsText: string;
  note: string;
  sortOrder: number;
  active: boolean;
}

const toDraft = (s: FaqSection): Draft => ({
  title: s.title,
  itemsText: s.items.join('\n'),
  note: s.note ?? '',
  sortOrder: s.sortOrder,
  active: s.active,
});

const EMPTY_DRAFT: Draft = {
  title: '',
  itemsText: '',
  note: '',
  sortOrder: 0,
  active: true,
};

function toPayload(d: Draft) {
  return {
    title: d.title.trim(),
    items: d.itemsText.split('\n').map((i) => i.trim()).filter(Boolean),
    note: d.note.trim() || null,
    sortOrder: d.sortOrder,
    active: d.active,
  };
}

export default function AdminSettings() {
  const [form, setForm] = useState<PlatformSettings | null>(null);
  /*
   * Снимок сохранённого: без него «Сохранить» одинаково выглядит и когда
   * сохранять нечего, и когда правки ждут. На длинной форме это разница
   * между «я уже нажал» и «я нажал в другом месте».
   */
  const [saved, setSaved] = useState<PlatformSettings | null>(null);
  const [busy, setBusy] = useState(false);

  /** Состояние бота: работает ли, под каким именем и через какой канал. */
  const [bot, setBot] = useState<{
    online: boolean;
    username: string | null;
    error: string | null;
    apiRoot: string;
  } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const [faq, setFaq] = useState<FaqSection[]>([]);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>({ ...EMPTY_DRAFT });

  async function loadFaq() {
    try {
      setFaq((await api.admin.faq()).items);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function loadBot() {
    // Молча: состояние бота — справка на этом экране, а не то, ради чего
    // сюда пришли, и сообщением об ошибке заслонять настройки незачем.
    await api.botStatus().then(setBot).catch(() => setBot(null));
  }

  useEffect(() => {
    api
      .settings()
      .then((s) => {
        setForm(s);
        setSaved(s);
      })
      .catch((e) => alert((e as Error).message));
    void loadFaq();
    void loadBot();
  }, []);

  /**
   * Проверка канала до сохранения.
   *
   * Проверяем ровно то, что набрано в поле, а не то, что уже сохранено:
   * иначе кнопка отвечала бы на другой вопрос, чем тот, который человек
   * задаёт, набрав новый адрес.
   */
  async function testProxy() {
    if (!form) return;
    const target = form.botProxyEnabled ? form.botProxyUrl.trim() : 'https://api.telegram.org';

    if (!target) {
      setTestResult({ ok: false, text: 'Укажите адрес зеркала' });
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.botTest(target);
      setTestResult(
        r.ok
          ? { ok: true, text: `Связь есть: @${r.username} через ${r.apiRoot}` }
          : { ok: false, text: `Не отвечает (${r.apiRoot}): ${r.error}` },
      );
    } catch (e) {
      setTestResult({ ok: false, text: (e as Error).message });
    } finally {
      setTesting(false);
    }
  }

  async function saveSection() {
    const payload = toPayload(draft);
    if (payload.title.length < 2) return alert('Укажите заголовок раздела');
    if (payload.items.length === 0) return alert('Добавьте хотя бы один пункт');

    setBusy(true);
    try {
      if (editing === 'new') await api.admin.createFaq(payload);
      else if (editing) await api.admin.updateFaq(editing, payload);
      setEditing(null);
      await loadFaq();
      notify('success');
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeSection(s: FaqSection) {
    if (!(await confirm(`Удалить раздел «${s.title}»?`))) return;
    setBusy(true);
    try {
      await api.admin.deleteFaq(s.id);
      await loadFaq();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      const saved = await api.admin.updateSettings({
        supportTelegram: form.supportTelegram.replace(/^@/, ''),
        supportPhone: form.supportPhone ?? '',
        supportHours: form.supportHours,
        supportNote: form.supportNote ?? '',
        faqEnabled: form.faqEnabled,
        etaEnabled: form.etaEnabled,
        registrationModerationEnabled: form.registrationModerationEnabled,
        cargoModerationEnabled: form.cargoModerationEnabled,
        transportModes: form.transportModes,
        bidExtendSeconds: form.bidExtendSeconds,
        botProxyEnabled: form.botProxyEnabled,
        botProxyUrl: form.botProxyUrl,
      });
      setForm(saved);
      setSaved(saved);
      notify('success');

      /*
       * Бот пересоздаётся после смены канала, и на это уходит секунда-две.
       * Перечитываем состояние с задержкой: показать «не отвечает» сразу
       * после сохранения значит соврать — он в этот момент поднимается.
       */
      setTestResult(null);
      window.setTimeout(() => void loadBot(), 2500);

      // Настройки закэшированы у всех открытых окон, включая это.
      alert('Сохранено. Кнопка поддержки обновится после перезагрузки приложения.');
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!form) return <div className="empty">Загрузка…</div>;

  // Сравнение целиком: полей дюжина, и перечислять их поимённо значит
  // однажды забыть новое — тогда полоса будет молчать при живой правке.
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const openFaq = faq.find((s) => s.id === editing) ?? null;

  return (
    <>
      {/*
       * Форма и её полоса сохранения — в общей обёртке.
       *
       * Полоса закреплена, а держится она в пределах родителя: без
       * обёртки родителем была вся страница, и «Всё сохранено» от
       * настроек висело бы поверх разделов справки, к которым не
       * относится.
       */}
      <div>
        <div className="card">
          <h3>Поддержка</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Кнопка видна на всех экранах. Если не заполнить ни Telegram, ни телефон —
            кнопка скрывается.
          </div>

          <div className="field">
            <label>Telegram поддержки</label>
            <div className="row" style={{ gap: 8 }}>
              <span className="meta">@</span>
              <input
                autoCapitalize="none"
                placeholder="support_logistics"
                value={form.supportTelegram}
                onChange={(e) =>
                  setForm({ ...form, supportTelegram: e.target.value.replace(/^@/, '') })
                }
              />
            </div>
            {form.supportTelegram && (
              <button
                className="secondary"
                // По содержимому: проверка ссылки — попутное действие, а во
                // всю ширину она выглядит как главное на экране.
                style={{ marginTop: 8, width: 'fit-content' }}
                onClick={() => openTelegram(`https://t.me/${form.supportTelegram}`)}
              >
                Проверить ссылку
              </button>
            )}
          </div>

          <div className="field">
            <label>Телефон</label>
            <input
              inputMode="tel"
              placeholder="+79001234567"
              value={form.supportPhone ?? ''}
              onChange={(e) => setForm({ ...form, supportPhone: e.target.value })}
            />
          </div>

          <div className="field">
            <label>Время работы</label>
            <input
              placeholder="Круглосуточно"
              value={form.supportHours}
              onChange={(e) => setForm({ ...form, supportHours: e.target.value })}
            />
          </div>

          <div className="field">
            <label>Заметка над контактами</label>
            <input
              placeholder="Отвечаем в течение 15 минут"
              value={form.supportNote ?? ''}
              onChange={(e) => setForm({ ...form, supportNote: e.target.value })}
            />
          </div>

          <div className="divider" />
          <h3>Что показывать</h3>

          <div className="chips">
            <button
              type="button"
              className={`chip ${form.faqEnabled ? 'on' : ''}`}
              onClick={() => setForm({ ...form, faqEnabled: !form.faqEnabled })}
            >
              Раздел «Справка»
            </button>
            <button
              type="button"
              className={`chip ${form.etaEnabled ? 'on' : ''}`}
              onClick={() => setForm({ ...form, etaEnabled: !form.etaEnabled })}
            >
              Сроки доставки в карточках
            </button>
          </div>

          <div className="divider" />
          <h3>Регистрация</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Включено — новый аккаунт (пароль или Telegram) видит только заглушку
            «на рассмотрении», пока администратор не активирует его вручную
            в разделе «Пользователи». Выключено — доступ открывается сразу,
            как сейчас.
          </div>
          <div className="chips">
            <button
              type="button"
              className={`chip ${form.registrationModerationEnabled ? 'on' : ''}`}
              onClick={() =>
                setForm({
                  ...form,
                  registrationModerationEnabled: !form.registrationModerationEnabled,
                })
              }
            >
              Модерация регистраций
            </button>
          </div>

          <div className="meta" style={{ marginTop: 'var(--s4)', marginBottom: 'var(--s2)' }}>
            Включено — новая заявка не идёт сразу в ленту: она ждёт статуса
            «на модерации», пока диспетчер не пропустит её в разделе
            «Диспетчерская» → «Модерация». Выключено — публикуется сразу.
          </div>
          <div className="chips">
            <button
              type="button"
              className={`chip ${form.cargoModerationEnabled ? 'on' : ''}`}
              onClick={() =>
                setForm({ ...form, cargoModerationEnabled: !form.cargoModerationEnabled })
              }
            >
              Модерация заявок
            </button>
          </div>

          <div className="divider" />
          <h3>Бот и Telegram</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Где-то <code>api.telegram.org</code> недоступен напрямую — у
            российских хостеров это обычное дело, и тогда бот молча не
            поднимается: команды и уведомления не работают, а биржа работает.
            Зеркало — обходной путь для такой машины. Переехали туда, где
            Telegram доступен, — выключите тумблер, и бот пойдёт напрямую.
          </div>

          {/* Состояние — рядом с настройкой, а не в логах: настраивают канал
              здесь, и результат должен быть виден здесь же. */}
          <div className={bot?.online ? 'notice notice--ok' : 'notice'}>
            <strong>
              {bot === null
                ? 'Состояние бота: загрузка…'
                : bot.online
                  ? `Бот работает: @${bot.username}`
                  : 'Бот не отвечает'}
            </strong>
            {bot && (
              <div className="meta" style={{ marginTop: 4 }}>
                Канал: <code>{bot.apiRoot}</code>
                {bot.error && <> · последняя ошибка: {bot.error}</>}
              </div>
            )}
          </div>

          <div className="chips" style={{ margin: '12px 0' }}>
            <button
              type="button"
              className={`chip ${form.botProxyEnabled ? 'on' : ''}`}
              onClick={() => setForm({ ...form, botProxyEnabled: !form.botProxyEnabled })}
            >
              Ходить через зеркало
            </button>
          </div>

          <div className="field">
            <label>Адрес зеркала</label>
            <input
              placeholder="http://172.27.0.1:8081"
              value={form.botProxyUrl}
              onChange={(e) => setForm({ ...form, botProxyUrl: e.target.value })}
            />
            <span className="meta">
              Пусто при включённом тумблере — то же, что выключено: обращаться
              в никуда хуже, чем ходить напрямую.
            </span>
          </div>

          {/* Проверка до сохранения: сохранить неверный адрес и остаться без
              бота, узнав об этом из тишины, — худший способ настраивать канал. */}
          <div className="chips" style={{ marginBottom: 12 }}>
            <button
              type="button"
              className="chip chip--ghost"
              disabled={testing}
              onClick={() => void testProxy()}
            >
              {testing ? 'Проверяю…' : 'Проверить связь'}
            </button>
          </div>
          {testResult && (
            <div className={testResult.ok ? 'notice notice--ok' : 'error'}>{testResult.text}</div>
          )}

          <div className="divider" />
          <h3>Торги</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Ставка, поданная в последние секунды, отодвигает конец торгов на
            столько же. Без этого выигрывает не лучшая цена, а самая точная
            секунда: ставку кладут в последний момент, и ответить сопернику
            нечем. Слишком большая добавка вредит с другой стороны — срок
            уезжает, а автор назначал его под подачу машины. Ноль отключает
            продление: торги кончаются ровно в объявленное время.
          </div>
          <div className="field" style={{ maxWidth: 260 }}>
            <label>Продление после ставки, секунд</label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={3600}
              value={form.bidExtendSeconds}
              onChange={(e) =>
                setForm({ ...form, bidExtendSeconds: Math.max(0, Number(e.target.value) || 0) })
              }
            />
          </div>

          <div className="divider" />
          <h3>Виды транспорта</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Что участники смогут выбрать при публикации заявки. Автомобиль
            включён всегда — на нём держится вся механика сделки: кузова,
            маршрут по дорогам, подбор попутного груза. Остальные режимы
            заведены как расширение: включайте их, когда площадка готова
            такую перевозку вести, иначе заявка появится, а довести её будет
            нечем.
          </div>
          <div className="chips">
            {TRANSPORT_MODES.map((m) => {
              const on = (form.transportModes ?? ['AUTO']).includes(m);
              const locked = m === 'AUTO';
              return (
                <button
                  key={m}
                  type="button"
                  className={`chip ${on ? 'on' : ''}`}
                  disabled={locked}
                  title={locked ? 'Автомобиль выключить нельзя' : TRANSPORT_MODE_HINT[m]}
                  onClick={() =>
                    setForm({
                      ...form,
                      transportModes: on
                        ? (form.transportModes ?? []).filter((x) => x !== m)
                        : [...(form.transportModes ?? ['AUTO']), m],
                    })
                  }
                >
                  {TRANSPORT_MODE_LABEL[m]}
                </button>
              );
            })}
          </div>

        </div>

        {/*
         * Полоса сохранения закреплена внизу: настроек шесть разделов, и
         * кнопка в самом конце означала, что после правки телефона поддержки
         * до неё надо пролистать торги, зеркало Telegram и виды транспорта.
         */}
        <div className="formbar">
          {/* Кнопка слева, как в разборе: правый нижний угол занят плавающей
              кнопкой помощи, и «Сохранить» уезжало ровно под неё. */}
          <button disabled={busy || !dirty} onClick={() => void save()}>
            Сохранить
          </button>
          <span className="meta">
            {dirty ? 'Есть несохранённые правки' : 'Всё сохранено'}
          </span>
        </div>
      </div>

      {/*
       * Разделы справки — последний список на этом экране, и живёт он по
       * тем же правилам, что вкладки: строка, разбор, одно действие.
       * Правка раздела переехала в разбор — прежде форма раскрывалась
       * прямо в списке и отодвигала его вниз, а закрыть её можно было
       * только «Отменой».
       */}
      <h3 className="sect">Разделы справки</h3>
      <div className="meta" style={{ marginBottom: 'var(--s3)' }}>
        Показываются в окне поддержки в порядке возрастания номера.
        Выключенный раздел остаётся в админке, но людям не виден.
      </div>

      <button
        className="anew"
        onClick={() => {
          setEditing('new');
          setDraft({ ...EMPTY_DRAFT, sortOrder: (faq.at(-1)?.sortOrder ?? 0) + 10 });
        }}
      >
        Добавить раздел
      </button>

      {faq.length === 0 && (
        <button
          className="secondary anew"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              setFaq((await api.admin.restoreFaq()).items);
            } catch (e) {
              alert((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Вернуть разделы по умолчанию
        </button>
      )}

      <AdminList
        rows={faq}
        keyOf={(s) => s.id}
        empty="Разделов справки нет"
        // Скрытый подсвечен: людям его не видно, и это ровно тот случай,
        // когда «почему в справке пусто» ищут глазами по списку.
        highlight={(s) => !s.active}
        action={{
          label: 'Открыть',
          onClick: (s) => {
            setEditing(s.id);
            setDraft(toDraft(s));
          },
        }}
        columns={[
          {
            title: 'Раздел',
            cell: (s) => (
              <>
                <strong>{s.title}</strong>
                <span className="meta" style={{ display: 'block' }}>
                  {s.items.slice(0, 2).join(' · ')}
                  {s.items.length > 2 ? ' …' : ''}
                </span>
              </>
            ),
          },
          {
            title: 'Пунктов',
            width: '110px',
            labelOnPhone: true,
            inline: true,
            cell: (s) => <span className="tabular">{s.items.length}</span>,
          },
          {
            title: 'Порядок',
            width: '110px',
            labelOnPhone: true,
            inline: true,
            cell: (s) => <span className="tabular">{s.sortOrder}</span>,
          },
          {
            title: 'Видимость',
            width: '130px',
            labelOnPhone: true,
            inline: true,
            cell: (s) => (s.active ? 'показан' : 'скрыт'),
          },
        ]}
      />

      <AdminSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Новый раздел' : (openFaq?.title ?? 'Раздел справки')}
        subtitle={
          openFaq && (
            <>
              пунктов {openFaq.items.length} · порядок {openFaq.sortOrder}
              {!openFaq.active && ' · скрыт'}
            </>
          )
        }
        actions={
          <>
            <button disabled={busy} onClick={() => void saveSection()}>
              Сохранить раздел
            </button>
            <button className="secondary" onClick={() => setEditing(null)}>
              Отмена
            </button>
            {openFaq && (
              <>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={async () => {
                    await api.admin.updateFaq(openFaq.id, { active: !openFaq.active });
                    await loadFaq();
                  }}
                >
                  {openFaq.active ? 'Скрыть' : 'Показать'}
                </button>
                <button
                  className="danger"
                  disabled={busy}
                  onClick={() => void removeSection(openFaq)}
                >
                  Удалить
                </button>
              </>
            )}
          </>
        }
      >
        <div className="field">
          <label>Заголовок</label>
          <input
            value={draft.title}
            placeholder="Например: Страхование"
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </div>

        <div className="field">
          <label>Пункты — по одному на строку</label>
          <textarea
            rows={6}
            value={draft.itemsText}
            placeholder={'Груз застрахован\nДопстрахование по запросу'}
            onChange={(e) => setDraft({ ...draft, itemsText: e.target.value })}
          />
        </div>

        <div className="field">
          <label>Приписка под списком</label>
          <input
            value={draft.note}
            placeholder="необязательно"
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          />
        </div>

        <div className="field-row">
          <div className="field">
            <label>Порядок</label>
            <NumberField
              decimal={false}
              negative={false}
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
            />
          </div>
          <div className="field">
            <label>Показывать</label>
            <button
              type="button"
              className={draft.active ? 'chip chip--on' : 'chip'}
              onClick={() => setDraft({ ...draft, active: !draft.active })}
            >
              {draft.active ? 'да' : 'нет'}
            </button>
          </div>
        </div>
      </AdminSheet>
    </>
  );
}
